import React from 'react';
import i18n from 'i18next';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { useForm } from '../../../../../../utils/validators';
import { ModerationAttendee } from '../../../../../../models';
import useModerationContext from '../../../../../../context/useModerationContext';

import { Button, Form, ToastContext, Date, Select } from '@weezevent/nacre';

import CountrySelector from '../../../../../../nacre/components/countrySelector';

const BirthdateField = ({ field, form, setForm, locale }) => {
    const dateValue = React.useMemo(() => {
        if (!form[field.source] || form[field.source] === 'None') {
            return '';
        }
        return moment(form[field.source], 'YYYY-MM-DD', true);
    }, [form]);

    return (
        <Form.Field>
            <Form.Label label={field.question} />
            <Date.Input
                value={dateValue}
                locale={locale}
                onChange={value => {
                    setForm(field.source, value);
                }}
                returnFormat={'YYYY-MM-DD'}
                disabled={!field.allow_modification}
            />
        </Form.Field>
    );
};

const CountryField = ({ field, form, setForm }) => (
    <Form.Field>
        <Form.Label label={field.question} />
        <CountrySelector
            name={form[field.source]}
            value={Boolean(form[field.source]) && form[field.source]}
            onChange={value => {
                setForm(field.source, value);
            }}
            disabled={!field.allow_modification}
        />
    </Form.Field>
);

const FormSelect = ({ field, form, setForm }) => {
    const { t } = useTranslation();
    const choices = React.useMemo(() => {
        return field.field.question_choices.map(obj => {
            return {
                key: obj.weez_id,
                text: obj.label,
                value: obj.weez_id
            };
        });
    }, [field, i18n]);

    return (
        <Form.Field>
            <Form.Label label={field.question} />
            <Select
                placeholder={t('ticket.common.action.select')}
                options={choices}
                value={parseInt(form[field.source])}
                onChange={value => setForm(field.source, value)}
                disabled={!field.allow_modification}
                {...field}
            />
        </Form.Field>
    );
};

const DefaultField = ({ field, form, setForm }) => (
    <Form.Field>
        <Form.Label label={field.question} />
        <Form.Input
            name={form[field.source]}
            value={form[field.source]}
            onChange={({ target: { value } }) => {
                setForm(field.source, value);
            }}
            disabled={!field.allow_modification}
        />
    </Form.Field>
);

export default function ModerationProfileAttendeeInformationsForm({ attendeeForm, setEditAttendee, organizationId, attendee, flowId, syncAttendee }) {
    const { t } = useTranslation();
    const locale = i18n.language;
    const [{ flow, removedAttendeesQuery }] = useModerationContext();
    const toast = React.useContext(ToastContext);
    const context = attendee.event_spam_moderation ? 'event' : 'attendee';

    const initialForm = React.useMemo(() => {
        return attendeeForm?.reduce((acc, { source, answer }) => {
            acc[source] = answer;
            return acc;
        }, {});
    }, [attendeeForm]);

    const [form, setForm, setFormObject] = useForm({ ...initialForm });

    const handleSubmit = React.useCallback(() => {
        const query = attendee.removed ? removedAttendeesQuery : {};
        let attendeeForm = new ModerationAttendee({ id: attendee.id, form: { ...form } });

        attendeeForm
            .save({ organizationId, flowId, query })
            .then(() => {
                setEditAttendee(false);
                syncAttendee();
                toast.success(t(`ticket.moderation.attendee.${context}Informations.toastSuccess`));
            })
            .catch(e => {
                toast.error(t(`ticket.moderation.attendee.${context}Informations.toastError`));
            });
    }, [form, removedAttendeesQuery, attendee, organizationId, flow, flowId]);

    return (
        <>
            {attendeeForm
                .filter(entry => !(entry.source === 'ws-50'))
                .map((field, index) => {
                    switch (field.field_type) {
                        case 'birthdate':
                            return <BirthdateField key={index} field={field} form={form} setForm={setForm} locale={locale} />;
                        case 'country':
                            return <CountryField key={index} field={field} form={form} setForm={setForm} />;
                        case 'select':
                            return <FormSelect key={index} field={field} form={form} setForm={setForm} />;
                        default:
                            return <DefaultField key={index} field={field} form={form} setForm={setForm} />;
                    }
                })}
            <Button.Group>
                <Button primary onClick={handleSubmit} label={t('ticket.common.action.save')} />
                <Button
                    onClick={() => {
                        setEditAttendee(false);
                        setFormObject({ ...initialForm });
                    }}
                    label={t('ticket.common.action.cancel')}
                />
            </Button.Group>
        </>
    );
}
